import { ConstraintsCategory } from "react-migration/layouts/map/Constraints/types";
import type { GenericLayer } from "../../types";
import { TupleOf } from "react-migration/lib/util/TupleOf";
import { ConstraintStatus } from "./ConstraintsContext/store";
import { DesignationSubTitleComponent } from "react-migration/layouts/designations/Designation";

export type CheckboxSchema = { key: string; showChildren?: boolean; defaultValue?: boolean };

type AttributeFilterOption = { value: string; display_name?: string };

export type AttributeFilter = {
  attribute: string;
  disableHatch?: boolean;
  options: AttributeFilterOption[];
};

export type ZoomPointScale = {
  zoom: TupleOf<number, 2>;
  pointScale: TupleOf<number, 2>;
};

export type CollisionFilter = {
  maxZoom?: number;
  /**
   * Maps designation feature properties to a numeric value to decide which feature should be displayed when they overlap.
   * Features have all matching values added together and must be in the -1000 - 1000 range once calculated. If no matching value is
   * available for any path in the map it defaults to a value of 0 for that item in the map
   */
  collisionPriorityMap?: Record<string, number | Record<string, number | Record<string, number>>>;
  collisionGroup?: string;
  collisionTestProps?: Record<string, unknown>;
};

export type DisplayByConfig = {
  /** Should the display by dropdown be shown. */
  showDropdown?: boolean;
  /** The list of attributes to display the layer by. */
  attributeOptions?: { attribute: string; label: string }[];
  /** Should there be an option to display layer by the data sub_category style. */
  categoryOption?: boolean;
  /** The label for the category option. */
  categoryOptionLabel?: string;
  /** The default display by value. */
  defaultDisplayBy?: string;
};

export type SingleConstraintLayerConfig = BaseConstraintLayerConfig & {
  rootCategory: ConstraintsCategory;
};

export type MultiConstraintLayerConfig = BaseConstraintLayerConfig & {
  rootCategories: ConstraintsCategory[];
};

type BaseConstraintLayerConfig = {
  /** If provided the map layer will be split into N layers, one for each root. */
  layerRoots?: string[];
  /** The schema of the category checkbox. */
  categorySchema: CheckboxSchema[];
  /**
   * Used to configure a "Display by" dropdown which will allow the user to
   * change the designation attribute used to style the layer.
   */
  displayBy?: DisplayByConfig;
  /**
   * Use to render attribute filters in the ControlPage.
   */
  attributeFilters?: AttributeFilter[];
  /**
   * Attributes to load from the API for use in things like StyleMap loading, designation item display, etc.
   */
  attributesToLoad?: string[];
  /*
   * Used to render status checkboxes in the ControlPage – e.g. Adopted, Emerging, etc.
   */
  statusSchema?: ConstraintStatus[];
  /** Should the constraint labels be shown on the map. */
  showConstraintLabels?: boolean;
  disableHatch?: boolean;
  showTitle?: boolean;
  minZoom?: number;
  /** The zoom level at which the stroke width is set to 0. */
  strokeThreshold?: number;
  maxZoom?: number;
  dedupePoints?: boolean;
  filterAreaByZoom?: boolean;
  zoomPointScale?: ZoomPointScale;
  intersectionThresholdMeters?: number;
  simplifyProfile?: SimplifyProfile;
  collisionFilter?: CollisionFilter;
  forcedRasterCategories?: string[];
  autoHighlight?: boolean;
  /**
   * This Config is designed to be serializable. Adding this pattern breaks this.
   * However, for now, we want to stress that this is specific to the constraints
   * layers. This allows us to achieve this. In the future, we want to find a more
   * fitting place to define this config without having to use layer config.
   */
  components?: {
    DesignationList?: {
      SubTitle?: DesignationSubTitleComponent;
    };
  };
};

export type ConstraintLayerConfig = SingleConstraintLayerConfig | MultiConstraintLayerConfig;

export const isSingleConstraintLayer = (
  layerConfig: ConstraintLayerConfig
): layerConfig is SingleConstraintLayerConfig => {
  return "rootCategory" in layerConfig;
};

export const isMultiConstraintLayer = (
  layerConfig: ConstraintLayerConfig
): layerConfig is MultiConstraintLayerConfig => {
  return "rootCategories" in layerConfig;
};

export type ConstraintsLayer = GenericLayer<ConstraintLayerConfig>;

export enum SimplifyProfile {
  LOW = "LOW",
  HIGH = "HIGH",
}
